<script>
/* eslint-disable no-console */
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import axios from "axios";
import { toast, variants } from "@/helpers/toaster";

export default {
  page: {
    title: "Bigz Net Prodavnica",
  },
  components: { PageHeader, Layout },
  props: {},
  data() {
    return {
      title: "Bigz Net Prodavnica",
      search: "",
      sort: "name_asc",
      orderFormItems: [],
      currency: "",
      cart: [],
      currentPage: 1,
      perPage: 12,
      maxQuantityPerItem: 99999,

      isCartCollapsed: true,

      showEditQuantityModal: false,
      editQuantityItem: null,
      editQuantityValue: 0,
      editQuantityError: "",

      itemNotes: {},

      currentOpenNoteTargetId: null,
    };
  },
  watch: {
    isCartCollapsed(newValue) {
      try {
        window.localStorage.setItem(
          "aribaCart.isCartCollapsed",
          newValue ? "1" : "0",
        );
      } catch (e) {
        // ignore
      }
    },
    search() {
      // Keep pagination intuitive when the result set changes.
      this.currentPage = 1;
    },
    sort() {
      // Sorting changes the visible order; keep pagination intuitive.
      this.currentPage = 1;
    },
  },
  computed: {
    sortLabel() {
      switch (this.sort) {
        case "name_desc":
          return "Naziv (Z-A)";
        case "code_asc":
          return "Šifra (A-Z)";
        case "code_desc":
          return "Šifra (Z-A)";
        case "name_asc":
        default:
          return "Naziv (A-Z)";
      }
    },
    totalPrice() {
      let price = 0;
      for (const orderFormItem of this.orderFormItems) {
        if (orderFormItem.amount) {
          price += orderFormItem.amount * orderFormItem.price;
        }
      }
      return price;
    },
    searchedOrderFormItems() {
      return this.orderFormItems.filter((orderFormItem) =>
        this.matchesSearch(orderFormItem, this.search),
      );
    },
    sortedOrderFormItems() {
      const items = (this.searchedOrderFormItems || []).slice();

      const getSortName = (item) =>
        this.normalizeSearchText((item && (item.name2 || item.name)) || "");
      const getSortCode = (item) =>
        this.normalizeSearchText((item && item.code) || "");
      const getTieBreaker = (item) =>
        Number(item && item.real_item_id != null ? item.real_item_id : 0) || 0;

      const compareText = (a, b) => {
        if (a === b) return 0;
        // numeric=true makes code/name sorting more natural when numbers exist.
        return String(a).localeCompare(String(b), undefined, {
          sensitivity: "base",
          numeric: true,
        });
      };

      const compareNum = (a, b) => {
        const na = Number(a) || 0;
        const nb = Number(b) || 0;
        return na === nb ? 0 : na < nb ? -1 : 1;
      };

      const direction = this.sort && this.sort.endsWith("_desc") ? -1 : 1;
      const mode = (this.sort || "name_asc").startsWith("code")
        ? "code"
        : "name";

      items.sort((left, right) => {
        let primary = 0;
        if (mode === "code") {
          primary = compareText(getSortCode(left), getSortCode(right));
        } else {
          primary = compareText(getSortName(left), getSortName(right));
        }

        if (primary !== 0) return primary * direction;
        return (
          compareNum(getTieBreaker(left), getTieBreaker(right)) * direction
        );
      });

      return items;
    },
    paginatedItems() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.sortedOrderFormItems.slice(start, end);
    },
    selectedItems() {
      return this.orderFormItems.filter((item) => item.amount > 0);
    },
  },
  async created() {
    this.$store.commit("loader/START_LOADING");

    try {
      const token = this.$route.query.token;
      const response = await axios.get("/ariba/shop", {
        params: {
          token: token,
        },
      });

      this.orderFormItems = response.data.products.map((item) => {
        // Find if this item exists in the cart
        const cartItem = response.data.cart.find(
          (c) => c.id === item.real_item_id || c.id === item.id,
        );

        return {
          ...item,
          amount: cartItem ? cartItem.amount : 0,
          desiredAmount: cartItem ? cartItem.amount : 0,
          isValid: true,
          validationMessage: "",
        };
      });
      this.cart = response.data.cart;
      this.currency = response.data.currency;
    } catch (e) {
      console.error("Error fetching items:", e);
    } finally {
      this.$store.commit("loader/END_LOADING");
    }
  },
  mounted() {
    try {
      const saved = window.localStorage.getItem("aribaCart.isCartCollapsed");
      if (saved === "0" || saved === "1") {
        this.isCartCollapsed = saved === "1";
      }
    } catch (e) {
      // ignore
    }

    document.addEventListener("click", this.onDocumentClick, true);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onDocumentClick, true);
  },
  methods: {
    normalizeSearchText(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim();
    },
    buildExactSearchQuery(value) {
      const raw = String(value || "")
        .replace(/\s+/g, " ")
        .trim();
      if (!raw) return "";

      // Our parser supports both quote styles, but not escaping.
      // Pick a wrapper that doesn't conflict; if both are present, strip them.
      if (!raw.includes('"')) return `"${raw}"`;
      if (!raw.includes("'")) return `'${raw}'`;

      const stripped = raw
        .replace(/["']/g, "")
        .replace(/\s+/g, " ")
        .trim();
      return stripped ? `"${stripped}"` : "";
    },
    focusSearchInput() {
      this.$nextTick(() => {
        const el = this.$refs.searchInput;
        if (!el) return;

        try {
          if (typeof el.focus === "function") el.focus();
          if (typeof el.select === "function") el.select();
        } catch (e) {
          // ignore
        }
      });
    },
    jumpToItemFromCart(item) {
      const label =
        (item && item.name2) ||
        (item && item.name) ||
        (item && item.code) ||
        (item && item.real_item_id != null ? String(item.real_item_id) : "");

      const query = this.buildExactSearchQuery(label);
      if (!query) return;

      this.isCartCollapsed = true;
      this.search = query;
      this.focusSearchInput();
    },
    openItemNote(product) {
      if (!product || product.real_item_id == null) return;

      const targetId = `note-btn-${product.real_item_id}`;

      if (this.currentOpenNoteTargetId === targetId) {
        this.hideItemNote(targetId);
        return;
      }

      if (
        this.currentOpenNoteTargetId &&
        this.currentOpenNoteTargetId !== targetId
      ) {
        this.hideItemNote(this.currentOpenNoteTargetId);
      }

      // Lazy-load first; popover content will update when loaded.
      this.ensureItemNoteLoaded(product);

      // Programmatically open the popover.
      // BootstrapVue convention: bv::show::popover with target id.
      if (this.$root && typeof this.$root.$emit === "function") {
        this.$root.$emit("bv::show::popover", targetId);
      }
    },
    hideItemNote(targetId) {
      if (!targetId) return;
      if (this.$root && typeof this.$root.$emit === "function") {
        this.$root.$emit("bv::hide::popover", targetId);
      }
      if (this.currentOpenNoteTargetId === targetId) {
        this.currentOpenNoteTargetId = null;
      }
    },
    onNoteShow(product) {
      if (!product || product.real_item_id == null) return;
      const targetId = `note-btn-${product.real_item_id}`;
      if (
        this.currentOpenNoteTargetId &&
        this.currentOpenNoteTargetId !== targetId
      ) {
        this.hideItemNote(this.currentOpenNoteTargetId);
      }

      this.ensureItemNoteLoaded(product);
    },
    onNoteShown(product) {
      if (!product || product.real_item_id == null) return;
      this.currentOpenNoteTargetId = `note-btn-${product.real_item_id}`;
    },
    onNoteHidden(product) {
      if (!product || product.real_item_id == null) return;
      const targetId = `note-btn-${product.real_item_id}`;
      if (this.currentOpenNoteTargetId === targetId) {
        this.currentOpenNoteTargetId = null;
      }
    },
    onDocumentClick(evt) {
      if (!this.currentOpenNoteTargetId) return;

      const target = evt && evt.target;
      if (!target || typeof target.closest !== "function") {
        this.hideItemNote(this.currentOpenNoteTargetId);
        return;
      }

      // Click inside the popover itself -> keep open.
      if (target.closest(".popover")) return;

      // Click on the note icon button (popover target) -> let BootstrapVue handle toggling.
      if (target.closest("button[id^='note-btn-']")) return;

      // Click on a product title (we open/toggle in openItemNote) -> don't auto-close here.
      if (target.closest(".product-name")) return;

      this.hideItemNote(this.currentOpenNoteTargetId);
    },
    parseSearchQuery(rawQuery) {
      const trimmed = String(rawQuery || "").trim();
      if (!trimmed) {
        return { mode: "all", exact: "", tokens: [] };
      }

      const isWrappedInDoubleQuotes =
        trimmed.length >= 2 && trimmed.startsWith('"') && trimmed.endsWith('"');
      const isWrappedInSingleQuotes =
        trimmed.length >= 2 && trimmed.startsWith("'") && trimmed.endsWith("'");

      if (isWrappedInDoubleQuotes || isWrappedInSingleQuotes) {
        const inner = trimmed.slice(1, -1);
        return { mode: "exact", exact: inner, tokens: [] };
      }

      const normalized = this.normalizeSearchText(trimmed);
      const tokens = normalized.split(" ").filter(Boolean);
      return { mode: "tokens", exact: "", tokens };
    },
    matchesSearch(item, rawQuery) {
      const parsed = this.parseSearchQuery(rawQuery);
      if (parsed.mode === "all") return true;

      const name2 = this.normalizeSearchText(item && item.name2);
      const name = this.normalizeSearchText(item && item.name);
      const brand = this.normalizeSearchText(item && item.brand);
      const code = this.normalizeSearchText(item && item.code);
      const id = this.normalizeSearchText(
        item && item.real_item_id != null ? String(item.real_item_id) : "",
      );

      if (parsed.mode === "exact") {
        const needle = this.normalizeSearchText(parsed.exact);
        if (!needle) return true;

        return (
          name2.includes(needle) ||
          name.includes(needle) ||
          brand.includes(needle) ||
          code.includes(needle) ||
          id.includes(needle)
        );
      }

      const haystack = this.normalizeSearchText(
        [
          item && item.name2,
          item && item.name,
          item && item.brand,
          item && item.code,
          item && item.real_item_id,
        ]
          .filter((v) => v !== null && v !== undefined)
          .join(" "),
      );

      return parsed.tokens.every((t) => haystack.includes(t));
    },
    filterItemNoteLines(lines) {
      const arr = Array.isArray(lines) ? lines : [];

      // Remove warranty-like lines such as: "garancija: 12 mes", "Garancija 24 meseci",
      // while keeping everything else. Conservative: require garanc* + digits + mes/mjes.
      const warrantyRegex = /^\s*garanc\w*\s*:?.*\d+.*\b(mes|mesec|meseci|mjes|mjesec|mjeseci)\b.*$/i;

      return arr
        .map((l) => (l == null ? "" : String(l)).trim())
        .filter(Boolean)
        .filter((l) => !warrantyRegex.test(l));
    },
    getItemNoteState(product) {
      const key =
        product && product.real_item_id != null
          ? String(product.real_item_id)
          : null;
      if (!key) {
        return { status: "idle", lines: [], error: "" };
      }

      return this.itemNotes[key] || { status: "idle", lines: [], error: "" };
    },
    async ensureItemNoteLoaded(product) {
      if (!product || product.real_item_id == null) return;

      const key = String(product.real_item_id);
      const existing = this.itemNotes[key];
      if (
        existing &&
        (existing.status === "loading" || existing.status === "loaded")
      ) {
        return;
      }

      this.$set(this.itemNotes, key, {
        status: "loading",
        lines: [],
        error: "",
      });
      try {
        const { data } = await axios.get("/ariba/item-note", {
          params: {
            token: this.$route.query.token,
            artikal: product.real_item_id,
          },
        });

        const lines = Array.isArray(data && data.napomena) ? data.napomena : [];
        const filtered = this.filterItemNoteLines(lines);
        this.$set(this.itemNotes, key, {
          status: "loaded",
          lines: filtered,
          error: "",
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Error fetching item note:", e);
        this.$set(this.itemNotes, key, {
          status: "error",
          lines: [],
          error: "Greška pri učitavanju opisa.",
        });
      }
    },
    getDesiredAmount(item) {
      const desired = Number(item && item.desiredAmount);
      if (Number.isFinite(desired)) return desired;

      const committed = Number(item && item.amount);
      return Number.isFinite(committed) ? committed : 0;
    },
    selectAllInput(evt) {
      const el = evt && evt.target;
      if (!el) return;

      // Ensure selection happens after focus/click settles.
      this.$nextTick(() => {
        try {
          if (typeof el.select === "function") el.select();
        } catch (e) {
          // ignore
        }
      });
    },
    parseDesiredAmount(rawValue) {
      if (rawValue === null || rawValue === undefined || rawValue === "") {
        return { ok: false, message: "Unesite količinu." };
      }

      const parsed = Number(rawValue);
      if (!Number.isFinite(parsed)) {
        return { ok: false, message: "Količina mora biti broj." };
      }
      if (!Number.isInteger(parsed)) {
        return { ok: false, message: "Količina mora biti ceo broj." };
      }

      return { ok: true, value: parsed };
    },
    validateDesiredAmount(item, desiredAmount) {
      const { ok, value, message } = this.parseDesiredAmount(desiredAmount);
      if (!ok) return { ok: false, message };

      const amount = value;
      if (amount < 0) {
        return { ok: false, message: "Količina mora biti veća ili jednaka 0." };
      }
      if (amount > this.maxQuantityPerItem) {
        return {
          ok: false,
          message: `Maksimalna količina je ${this.maxQuantityPerItem}.`,
        };
      }

      const increment = Number(item.number_in_package) || 1;
      if (increment > 1 && amount !== 0 && amount % increment !== 0) {
        return {
          ok: false,
          message: `Količina mora biti deljiva sa ${increment}.`,
        };
      }

      return { ok: true, value: amount };
    },
    async saveCart() {
      const items = (this.selectedItems || []).map((item) => ({
        id: item.id,
        amount: item.amount,
      }));

      try {
        await axios.put(
          "/ariba/cart",
          {
            items,
          },
          {
            params: {
              token: this.$route.query.token,
            },
          },
        );

        return true;
      } catch (e) {
        console.error("Error saving cart:", e);

        return false;
      }
    },
    async applyDesiredAmount(item) {
      const result = this.validateDesiredAmount(item, item.desiredAmount);
      if (!result.ok) {
        this.$set(item, "isValid", false);
        this.$set(item, "validationMessage", result.message);
        return;
      }

      const previousAmount = Number(item.amount) || 0;

      this.$set(item, "amount", result.value);
      this.$set(item, "desiredAmount", result.value);
      this.$set(item, "isValid", true);
      this.$set(item, "validationMessage", "");

      const saved = await this.saveCart();
      if (!saved) return;

      const nextAmount = result.value;
      if (previousAmount === 0 && nextAmount > 0) {
        toast(
          "Artikal je dodat u korpu.",
          variants.SUCCESS,
          "b-toaster-bottom-right",
          5000,
        );
      } else if (previousAmount > 0 && nextAmount === 0) {
        toast(
          "Artikal je uklonjen iz korpe.",
          variants.SUCCESS,
          "b-toaster-bottom-right",
          5000,
        );
      } else if (previousAmount !== nextAmount) {
        toast(
          "Količina je ažurirana.",
          variants.SUCCESS,
          "b-toaster-bottom-right",
          5000,
        );
      }
    },
    openEditQuantityModal(item) {
      this.editQuantityItem = item;
      this.editQuantityValue = Number(item.amount) || 0;
      this.editQuantityError = "";
      this.showEditQuantityModal = true;
    },
    onEditQuantityOk(bvModalEvt) {
      if (bvModalEvt && typeof bvModalEvt.preventDefault === "function") {
        bvModalEvt.preventDefault();
      }

      if (!this.editQuantityItem) {
        this.showEditQuantityModal = false;
        return;
      }

      const result = this.validateDesiredAmount(
        this.editQuantityItem,
        this.editQuantityValue,
      );
      if (!result.ok) {
        this.editQuantityError = result.message;
        return;
      }

      this.$set(this.editQuantityItem, "desiredAmount", result.value);
      this.applyDesiredAmount(this.editQuantityItem);
      this.showEditQuantityModal = false;
    },
    openWindow(itemId) {
      const url = `https://www.bigznet.rs/image/catalog/net/${itemId}`;
      window.open(
        url,
        "_blank",
        "toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=800,height=600,popup",
      );
    },
    calculatePercentage(current, total) {
      if (total === 0) {
        return 0;
      }
      const percentage = (current / total) * 100;
      return (percentage > 100 ? 100 : percentage.toFixed(2)) + "%";
    },
    addToCart(item) {
      const increment = Number(item.number_in_package) || 1;
      const base = this.getDesiredAmount(item);
      const next = base + increment;
      this.$set(item, "desiredAmount", next);

      const result = this.validateDesiredAmount(item, next);
      this.$set(item, "isValid", result.ok);
      this.$set(item, "validationMessage", result.ok ? "" : result.message);
    },
    removeFromCart(item) {
      const decrement = Number(item.number_in_package) || 1;
      const base = this.getDesiredAmount(item);
      const next = Math.max(0, base - decrement);
      this.$set(item, "desiredAmount", next);

      const result = this.validateDesiredAmount(item, next);
      this.$set(item, "isValid", result.ok);
      this.$set(item, "validationMessage", result.ok ? "" : result.message);
    },
    removeItemFromCart(item) {
      this.$set(item, "desiredAmount", 0);
      this.applyDesiredAmount(item);
    },
    getImageUrl(itemId) {
      return `https://www.bigznet.rs/image/cache/catalog/net/${itemId}-200x200.jpg`;
    },
    onImageError(event, itemId) {
      const fallbackUrl = `https://www.bigznet.rs/image/catalog/net/${itemId}`;
      if (event.target.src !== fallbackUrl) {
        event.target.src = fallbackUrl;
      }
    },
    async checkout() {
      this.$store.commit("loader/START_LOADING");
      try {
        const response = await axios.post(
          "/ariba/checkout",
          {},
          {
            params: {
              token: this.$route.query.token,
            },
          },
        );
        console.log("Checkout response:", response.data);
        // Create a hidden form and submit it to the browser_form_post_url
        const form = document.createElement("form");
        form.method = "POST";
        form.action = response.data.browser_form_post_url;

        // Add the base64-encoded cXML
        const cxmlInput = document.createElement("input");
        cxmlInput.type = "hidden";
        cxmlInput.name = "cxml-base64"; // IMPORTANT: Use 'cxml-base64', not 'cxml'
        cxmlInput.value = response.data.cxml_base64; // Use base64 encoded value
        form.appendChild(cxmlInput);

        // Submit the form
        document.body.appendChild(form);
        form.submit();
      } catch (error) {
        console.error("Checkout failed:", error);
        this.$store.commit("loader/END_LOADING");
      }
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title">
      <button
        class="btn"
        :class="isCartCollapsed ? 'btn-primary' : 'btn-outline-secondary'"
        type="button"
        @click="isCartCollapsed = !isCartCollapsed"
        :title="isCartCollapsed ? 'Prikaži korpu' : 'Sakrij korpu'"
        style="white-space: nowrap;"
      >
        <i class="mdi mdi-cart"></i>
        <span class="ms-2">Korpa ({{ selectedItems.length }})</span>
        <i
          class="ms-2 mdi"
          :class="isCartCollapsed ? 'mdi-chevron-left' : 'mdi-chevron-right'"
        ></i>
      </button>
    </PageHeader>

    <div class="row">
      <div class="col-12 col-lg">
        <div class="search-box chat-search-box mb-2">
          <div class="position-relative">
            <input
              input
              v-model="search"
              ref="searchInput"
              type="text"
              class="form-control"
              placeholder='Pretraži... (npr. "tačan naziv" ili po ključnim rečima)'
            />
            <i class="bx bx-search-alt search-icon"></i>
            <i class="bx bx-x clear-icon" @click="search = ''"></i>
          </div>
        </div>
      </div>
      <div class="col-12 col-lg-auto">
        <b-dropdown
          right
          variant="outline-info"
          size="md"
          class="mb-2"
          toggle-class="w-100 d-flex align-items-center justify-content-between"
          style="min-width: 220px;"
        >
          <template #button-content>
            <span class="d-flex align-items-center">
              <i class="mdi mdi-filter-outline me-1"></i>
              <span>{{ sortLabel }}</span>
            </span>
            <i class="mdi mdi-chevron-down ms-2"></i>
          </template>

          <b-dropdown-item-button @click="sort = 'name_asc'">
            Naziv (A-Z)
          </b-dropdown-item-button>
          <b-dropdown-item-button @click="sort = 'name_desc'">
            Naziv (Z-A)
          </b-dropdown-item-button>
          <b-dropdown-divider />
          <b-dropdown-item-button @click="sort = 'code_asc'">
            Šifra (A-Z)
          </b-dropdown-item-button>
          <b-dropdown-item-button @click="sort = 'code_desc'">
            Šifra (Z-A)
          </b-dropdown-item-button>
        </b-dropdown>
      </div>
    </div>

    <div
      class="row"
      style="gap: 20px; align-items: flex-start; min-height: calc(100vh - 300px);"
    >
      <!-- Products Grid -->
      <div
        class="col-12 col-lg"
        style="flex: 1; min-width: 0; display: flex; flex-direction: column; order: -1;"
      >
        <div
          class="card flex-fill"
          style="flex: 1; display: flex; flex-direction: column;"
        >
          <div
            class="card-body card-altered"
            style="flex: 1; display: flex; flex-direction: column;"
          >
            <div class="products-grid">
              <div
                v-for="product in paginatedItems"
                :key="product.real_item_id"
                class="product-card"
              >
                <div class="product-image">
                  <img
                    :src="getImageUrl(product.real_item_id)"
                    :alt="product.name2"
                    @click.prevent="openWindow(product.real_item_id)"
                    @error="onImageError($event, product.real_item_id)"
                    style="cursor: pointer;"
                  />
                </div>
                <div class="product-info">
                  <div class="product-name-row">
                    <h6
                      class="product-name"
                      @click="openItemNote(product)"
                      title="Prikaži opis"
                    >
                      {{ product.name2 }}
                    </h6>
                    <button
                      v-if="product.real_item_id"
                      :id="'note-btn-' + product.real_item_id"
                      class="btn btn-sm btn-link p-0 ms-2"
                      type="button"
                      title="Opis"
                    >
                      <i
                        class="mdi mdi-information-outline ariba-note-icon"
                      ></i>
                    </button>
                    <b-popover
                      v-if="product.real_item_id"
                      :target="'note-btn-' + product.real_item_id"
                      triggers="click"
                      placement="auto"
                      custom-class="ariba-item-note-popover"
                      @show="onNoteShow(product)"
                      @shown="onNoteShown(product)"
                      @hidden="onNoteHidden(product)"
                    >
                      <template #title>Opis artikla</template>

                      <div
                        v-if="getItemNoteState(product).status === 'loading'"
                      >
                        Učitavanje...
                      </div>
                      <div
                        v-else-if="getItemNoteState(product).status === 'error'"
                        class="text-danger"
                      >
                        {{ getItemNoteState(product).error }}
                      </div>
                      <div v-else-if="getItemNoteState(product).lines.length">
                        <ul class="ariba-item-note-list">
                          <li
                            v-for="(line, idx) in getItemNoteState(product)
                              .lines"
                            :key="idx"
                          >
                            {{ line }}
                          </li>
                        </ul>
                      </div>
                      <div v-else class="text-muted">Bez opisa.</div>
                    </b-popover>
                  </div>
                  <p class="product-code">
                    {{ product.code }}/ID-{{ product.real_item_id }}
                  </p>
                  <p class="product-meta">
                    <span v-if="product.brand" class="meta-item">
                      Proizvodjač: {{ product.brand }}
                    </span>
                    <span v-if="product.jm" class="meta-item"
                      >JM: {{ product.jm }}</span
                    >
                    <span v-if="product.number_in_package" class="meta-item">
                      PAK: {{ product.number_in_package }}
                    </span>
                  </p>
                  <p class="product-price">
                    {{ product.price }} {{ currency }}
                  </p>
                </div>
                <div class="product-quantity">
                  <button
                    class="btn btn-sm btn-outline-danger"
                    type="button"
                    @click="removeFromCart(product)"
                    :disabled="getDesiredAmount(product) <= 0"
                    title="Smanji količinu"
                  >
                    -
                  </button>
                  <input
                    class="form-control form-control-sm quantity-input quantity-input--inline"
                    type="number"
                    min="0"
                    :max="maxQuantityPerItem"
                    v-model.number="product.desiredAmount"
                    placeholder="Količina"
                    title="Unesite količinu"
                    @focus="selectAllInput"
                    @click="selectAllInput"
                  />
                  <button
                    class="btn btn-sm btn-outline-success"
                    type="button"
                    @click="addToCart(product)"
                    title="Povećaj količinu"
                  >
                    +
                  </button>
                </div>

                <div class="mt-2">
                  <button
                    class="btn btn-sm btn-primary btn-block mt-2"
                    type="button"
                    @click="applyDesiredAmount(product)"
                  >
                    Dodaj u korpu
                  </button>
                  <small
                    v-if="product.validationMessage"
                    class="text-danger"
                    style="display: block; margin-top: 6px;"
                  >
                    {{ product.validationMessage }}
                  </small>
                </div>
              </div>
            </div>

            <nav
              aria-label="Page navigation"
              class="mt-2"
              style="margin-top: auto;"
            >
              <b-pagination
                v-model="currentPage"
                :total-rows="searchedOrderFormItems.length"
                :per-page="perPage"
                align="center"
              ></b-pagination>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!isCartCollapsed">
      <div class="ariba-cart-backdrop" @click="isCartCollapsed = true"></div>

      <div class="ariba-cart-drawer" @click.stop>
        <div class="ariba-cart-drawer__inner">
          <div
            class="card"
            style="height: 100%; display: flex; flex-direction: column;"
          >
            <div class="card-header">
              <div class="d-flex align-items-center justify-content-between">
                <h5 class="mb-0">Korpa ({{ selectedItems.length }})</h5>
                <button
                  class="btn btn-sm btn-outline-secondary"
                  type="button"
                  @click="isCartCollapsed = true"
                  title="Sakrij korpu"
                >
                  <i class="mdi mdi-chevron-right"></i>
                </button>
              </div>
            </div>
            <div
              class="card-body"
              style="flex: 1; display: flex; flex-direction: column; min-height: 0;"
            >
              <div
                v-if="selectedItems.length > 0"
                class="selected-items-list"
                style="flex: 1; min-height: 0;"
              >
                <div
                  v-for="item in selectedItems"
                  :key="item.real_item_id"
                  class="selected-item-row"
                >
                  <a
                    class="item-name"
                    href="#"
                    @click.prevent="jumpToItemFromCart(item)"
                    title="Pronađi ovaj artikal"
                  >
                    {{ item.name2 }}
                  </a>
                  <span class="item-amount">x{{ item.amount }}</span>
                  <span class="item-price"
                    >{{ (item.amount * item.price).toFixed(2) }}
                    {{ currency }}</span
                  >
                  <div class="item-actions">
                    <button
                      class="btn btn-sm btn-outline-primary"
                      type="button"
                      @click="openEditQuantityModal(item)"
                      title="Izmeni količinu"
                    >
                      <i class="mdi mdi-pencil"></i>
                    </button>
                    <button
                      class="btn btn-sm btn-outline-danger"
                      @click="removeItemFromCart(item)"
                      title="Ukloni iz korpe"
                    >
                      <i class="mdi mdi-close"></i>
                    </button>
                  </div>
                  <small
                    v-if="item.validationMessage"
                    class="text-danger"
                    style="display: block;"
                  >
                    {{ item.validationMessage }}
                  </small>
                </div>
              </div>
              <div v-else class="text-center text-muted py-5">
                Korpa je prazna
              </div>
              <div class="total-price mt-3 text-right">
                <strong
                  >Ukupno: {{ totalPrice.toFixed(2) }} {{ currency }}</strong
                >
              </div>
            </div>

            <div class="p-3" style="border-top: 1px solid #eee;">
              <b-button
                type="button"
                variant="success"
                @click="checkout()"
                :disabled="selectedItems.length === 0"
                class="w-100"
                >Poruči</b-button
              >
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal
      v-model="showEditQuantityModal"
      title="Izmeni količinu"
      size="sm"
      centered
      ok-title="Sačuvaj"
      cancel-title="Otkaži"
      @ok="onEditQuantityOk"
      @hidden="editQuantityError = ''"
    >
      <div v-if="editQuantityItem">
        <div class="mb-2">
          <div class="font-weight-bold">
            {{ editQuantityItem.name2 || editQuantityItem.code || "Artikal" }}
          </div>
          <div class="text-muted" style="font-size: 12px;">
            <span v-if="editQuantityItem.brand" style="display: block;">
              Proizvodjač: {{ editQuantityItem.brand }}
            </span>
            <span v-if="editQuantityItem.number_in_package">
              PAK: {{ editQuantityItem.number_in_package }}
            </span>
          </div>
        </div>

        <b-form-input
          v-model.number="editQuantityValue"
          type="number"
          min="0"
          :max="maxQuantityPerItem"
          placeholder="Količina"
          @focus="selectAllInput"
          @click="selectAllInput"
        />

        <small
          v-if="editQuantityError"
          class="text-danger"
          style="display: block; margin-top: 8px;"
        >
          {{ editQuantityError }}
        </small>
      </div>
    </b-modal>
  </Layout>
</template>

<style scoped>
.search-box .form-control {
  border-radius: 0.25rem;
}

.card-altered {
  overflow: auto;
}

.gap-2 {
  gap: 0.5rem;
}

.card.flex-fill {
  display: flex;
  flex-direction: column;
}

.card.flex-fill .card-body {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.product-card {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition: box-shadow 0.3s;
}

.product-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.product-image {
  width: 100%;
  height: 150px;
  overflow: hidden;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
}

.product-image img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.product-info {
  flex: 1;
}

.product-name {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  white-space: normal;
  word-break: break-word;
}

.product-name-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.ariba-note-icon {
  font-size: 20px;
  line-height: 1;
}

.ariba-item-note-popover {
  max-width: 360px;
}

.ariba-item-note-list {
  padding-left: 16px;
  margin: 0;
}

.product-code {
  margin: 5px 0 0;
  font-size: 12px;
  color: #999;
}

.product-meta {
  margin: 5px 0 0;
  font-size: 11px;
  color: #666;
}

.meta-item {
  display: block;
}

.ms-2 {
  margin-left: 0.5rem;
}

.ariba-cart-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  z-index: 1020;
}

.ariba-cart-drawer {
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: min(560px, 92vw);
  z-index: 1030;
  padding: 12px;
}

.ariba-cart-drawer__inner {
  height: 100%;
}

.product-price {
  margin: 5px 0 0;
  font-size: 14px;
  font-weight: 700;
  color: #28a745;
}

.product-quantity {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
}

.quantity-input {
  text-align: center;
}

.quantity-input--inline {
  width: 90px;
}

/* Hide number input spinners (Chrome/Safari/Edge) */
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Hide number input spinners (Firefox) */
input[type="number"] {
  appearance: textfield;
  -moz-appearance: textfield;
}

.btn-sm {
  padding: 4px 8px;
  font-size: 12px;
}

.selected-items-list {
  flex: 1;
  overflow-y: auto;
}

.selected-item-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 0;
  border-bottom: 1px solid #eee;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
}

.selected-item-row:last-child {
  border-bottom: none;
}

.item-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-wrap: auto;
  color: #0d6efd;
  text-decoration: none;
}

.item-name:hover {
  text-decoration: underline;
}

.item-amount {
  flex-shrink: 0;
  white-space: nowrap;
  font-weight: 600;
}

.item-price {
  flex-shrink: 0;
  white-space: nowrap;
  font-weight: 600;
  text-align: right;
}

.item-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.flex-grow-1 {
  flex: 1;
}
</style>
